import React, { useEffect, useState } from 'react';
import { Loader2, Flag, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { fmtMoney } from '@/lib/officialsApi';

export const PAY_STATUS = {
  scheduled: ['Scheduled', 'bg-slate-100 text-slate-600'],
  pending_verification: ['Pending verification', 'bg-amber-100 text-amber-800'],
  earned: ['Earned · needs approval', 'bg-sky-100 text-sky-800'],
  approved: ['Approved', 'bg-indigo-100 text-indigo-800'],
  partially_paid: ['Partially paid', 'bg-violet-100 text-violet-800'],
  paid: ['Paid', 'bg-emerald-100 text-emerald-800'],
  no_show: ['Did not attend', 'bg-red-50 text-red-700'],
  replaced: ['Replaced', 'bg-slate-100 text-slate-500'],
  cancelled: ['Cancelled', 'bg-slate-100 text-slate-500'],
};
export const STATUS_FILTERS = [['', 'All statuses'], ['scheduled', 'Scheduled'], ['pending_verification', 'Pending verification'],
  ['earned', 'Earned (needs approval)'], ['approved', 'Approved (unpaid)'], ['partially_paid', 'Partially paid'], ['paid', 'Paid'],
  ['outstanding', 'Outstanding'], ['needs_review', 'Needs review'], ['no_rate', 'No rate set'], ['no_show', 'Did not attend'],
  ['replaced', 'Replaced'], ['cancelled', 'Cancelled']];
export const ADJ_LABEL = { rate_adjustment: 'Rate Adjustment', travel: 'Travel Allowance', additional: 'Additional Assignment Fee', bonus: 'Bonus', cancellation: 'Cancellation Fee',
  deduction: 'Deduction', manual: 'Manual Adjustment' };
export const METHOD_LABEL = { bank_transfer: 'Bank Transfer', cash: 'Cash', cheque: 'Cheque', other: 'Other' };

export const BATCH_STATUS = {
  draft: ['Draft', 'bg-slate-100 text-slate-700'], approving: ['Approving…', 'bg-amber-100 text-amber-800'],
  approved: ['Approved · unpaid', 'bg-indigo-100 text-indigo-800'], partially_paid: ['Partially paid', 'bg-violet-100 text-violet-800'],
  paid: ['Paid', 'bg-emerald-100 text-emerald-800'],
};

export const PayStatusBadge = ({ status, testId, map = PAY_STATUS, label: override }) => {
  const [label, cls] = map[status] || [status, 'bg-slate-100 text-slate-600'];
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${cls}`} data-testid={testId}>{override || label}</span>;
};

export const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1.5 ${className}`}><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>
);

const FORMAT_LABEL = { pdf: 'PDF', csv: 'CSV', xlsx: 'Excel (.xlsx)' };
export const DownloadMenu = ({ onPick, label = 'Export', testId = 'download-menu', formats = ['pdf', 'csv', 'xlsx'], size = 'sm' }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size={size} variant="outline" data-testid={testId}><Download className="w-4 h-4 mr-1.5" />{label}</Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {formats.map(f => <DropdownMenuItem key={f} onClick={() => onPick(f)} data-testid={`${testId}-${f}`}>{FORMAT_LABEL[f]}</DropdownMenuItem>)}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Money = ({ minor, currency, className = '', testId }) => (
  <span className={`tabular-nums ${minor < 0 ? 'text-red-600' : ''} ${className}`} data-testid={testId}>{fmtMoney(minor, currency)}</span>
);

export const lineTitle = (l) => (l.kind === 'adjustment'
  ? `${ADJ_LABEL[l.adj_type] || 'Adjustment'} · ${l.description || ''}`
  : `${l.game?.team_a_name || 'TBD'} vs ${l.game?.team_b_name || 'TBD'}`);

export const FlagIcon = ({ l }) => (l.open_flags?.length ? (
  <span title={l.open_flags.map(f => f.message).join('\n')} className="text-red-600" data-testid={`line-flag-${l.id}`}><Flag className="w-3.5 h-3.5" /></span>
) : null);

export const MetricCard = ({ label, minor, currency, count, tone = 'text-[#00247D]', sub, testId, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testId}
    className={`text-left rounded-2xl border bg-white px-4 py-3.5 transition-colors ${onClick ? 'hover:border-[#00247D]/40 cursor-pointer' : 'cursor-default'}`}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl sm:text-3xl font-black leading-none tabular-nums ${tone}`} style={{ fontFamily: 'Barlow Condensed' }}>
      {count !== undefined ? count : fmtMoney(minor, currency)}
    </div>
    {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
  </button>
);

export const Panel = ({ title, desc, actions, children, testId }) => (
  <section className="bg-white border rounded-2xl" data-testid={testId}>
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b">
      <div className="mr-auto">
        <h3 className="text-lg font-black uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h3>
        {desc && <p className="text-xs text-slate-500">{desc}</p>}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

export const Loading = () => <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

// Generic confirm dialog with optional reason + amount (fee edits, pay anyway, reversals, voids).
export const ReasonDialog = ({ open, title, description, confirmLabel = 'Confirm', danger, withAmount, amountLabel = 'Amount',
  defaultAmount = '', requireReason = true, reasonLabel = 'Reason', onClose, onSubmit, testId = 'reason-dialog' }) => {
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(''); setAmount(defaultAmount); } }, [open, defaultAmount]);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit({ reason: reason.trim(), amount }); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className={danger ? 'text-red-600' : 'text-[#00247D]'}>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {withAmount && (
          <div className="space-y-1.5"><Label className="text-xs">{amountLabel}</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid={`${testId}-amount`} /></div>
        )}
        <div className="space-y-1.5"><Label className="text-xs">{reasonLabel}{requireReason ? '' : ' (optional)'}</Label>
          <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} data-testid={`${testId}-reason`} /></div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || (requireReason && !reason.trim())} onClick={submit} data-testid={`${testId}-confirm`}
            className={danger ? 'bg-red-600 hover:bg-red-700' : 'bg-[#00247D] hover:bg-[#001A5C]'}>
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
